import React, { useMemo, useCallback } from 'react';
import { Empty } from '@sm/ide-components';
import dayjs from 'dayjs';
import { useChatStore } from '@/store';
import { ChevronRightIcon } from '@/assets/SVGIcon';
import type { ChatSession } from '@/types/chat';
import { t } from '@/i18n';
import { mindMentionDisplayText } from '@/utils/mindHost';
import './index.less';

interface HistoryPanelProps {
  onSelect: (sessionId: string) => void;
  disabled?: boolean;
}

interface HistoryGroup {
  key: string;
  label: string;
  sessions: ChatSession[];
}

function groupSessionsByDate(sessions: ChatSession[]): HistoryGroup[] {
  const groups = new Map<string, ChatSession[]>();
  const order: string[] = [];
  const today = dayjs().startOf('day');
  const yesterday = today.subtract(1, 'day');

  const sorted = [...sessions].sort((a, b) => b.updatedAt - a.updatedAt);

  for (const session of sorted) {
    const d = dayjs(session.updatedAt);
    let key: string;
    if (d.isSame(today, 'day')) {
      key = 'today';
    } else if (d.isSame(yesterday, 'day')) {
      key = 'yesterday';
    } else {
      key = d.format('YYYY.MM.DD');
    }

    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(session);
  }

  return order.map((key) => ({
    key,
    label:
      key === 'today'
        ? t('history.today')
        : key === 'yesterday'
          ? t('history.yesterday')
          : key,
    sessions: groups.get(key)!,
  }));
}

const HistoryPanel: React.FC<HistoryPanelProps> = ({ onSelect, disabled }) => {
  const sessions = useChatStore((s) => s.historySessions);
  const loading = useChatStore((s) => s.historyLoading);
  const hasMore = useChatStore((s) => s.historyHasMore);
  const loadHistory = useChatStore((s) => s.loadHistory);
  const groups = useMemo(() => groupSessionsByDate(sessions), [sessions]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!hasMore || loading) return;
      const el = e.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
        void loadHistory();
      }
    },
    [hasMore, loading, loadHistory],
  );

  return (
    <div className="history-panel">
      <div className="history-panel__title">{t('history.title')}</div>

      <div className="history-panel__list" onScroll={handleScroll}>
        {groups.length === 0 && !loading ? (
          <Empty
            className="history-panel__empty"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('history.empty')}
          />
        ) : (
          <>
            {groups.map((group) => (
              <section key={group.key} className="history-panel__group">
                <div className="history-panel__date">{group.label}</div>
                <div className="history-panel__cards">
                  {group.sessions.map((session) => (
                    <button
                      key={session.id}
                      type="button"
                      className="history-panel__item"
                      disabled={disabled}
                      onClick={() => onSelect(session.id)}
                    >
                      <span className="history-panel__item-title">{mindMentionDisplayText(session.title)}</span>
                      <ChevronRightIcon />
                    </button>
                  ))}
                </div>
              </section>
            ))}
            {loading && <div className="history-panel__loading" />}
          </>
        )}
      </div>
    </div>
  );
};

export default HistoryPanel;
